import React, { useState, useEffect, useContext, useRef } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { MessageSquare, Inbox, Users, Briefcase, ArrowUpDown } from "lucide-react";
import { ProfileContext } from "../../App";
import {
  GET_ROLE_TOPICS,
  GET_CANDIDATE_TOPICS,
} from "../../GraphQL/messages/queries";
import { CREATE_CANDIDATE_RESOLVING_MESSAGE, CREATE_ROLE_RESOLVING_MESSAGE } from '../../GraphQL/messages/mutations';
import { JobTopic, CandidateTopic, EToastPosition, EToastColorStatus } from "../../interfaces";
import { useNavigate, useParams } from "react-router-dom";
import FloatingChatMessage from "./FloatingChatMessage";
import MessageRow from "./MessageRow";
import MessageCard from "./MessageCard";
import MessagesTableSkeleton from "./MessagesTableSkeleton";
import { compareSortingData } from "../../utils";
import useCheckMobile from "../../utils/hooks/useCheckMobile";
import CustomPagination, { CustomPaginationType } from "../Global/CustomPagination";
import Notification from "../Notification";
import { cn } from "@/lib/utils";
import ChevronTableHeader from "../Icons/ChevronTableHeader";

import guide from "../../assets/role_relay_user_guide.pdf"

interface Props {
  user: any;
  activeTab?: string
}

const Messages = ({ user, activeTab }: Props) => {
  const { topicType } = useParams();
  const client = useApolloClient();
  const navigate = useNavigate();
  const { width } = useCheckMobile();
  const isMobile = width < 960;
  const [jobTopics, setJobTopics] = useState<JobTopic[]>([]);
  const [candidateTopics, setCandidateTopics] = useState<CandidateTopic[]>([]);

  const [tabKey, setTabKey] = useState(activeTab);
  const [isOpenChat, setIsOpenChat] = useState(false);
  const [topicItem, setTopicItem] = useState("");
  const [hideRoleResolved, setHideRoleResolved] = useState(true);
  const [hideCandidateResolved, setHideCandidateResolved] = useState(true);
  const [roleHasNext, setRoleHasNext] = useState(false);
  const [candidateHasNext, setCandidateHasNext] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const initSortingRoles = { jobTitle: false, lastCommentCreatedAt: false };
  const [activeSortRoles, setActiveSortRoles] = useState(initSortingRoles);
  const [isRoleASC, setIsRoleASC] = useState(true);
  const resetRoleSort = () => setActiveSortRoles(initSortingRoles);

  const initSortingCandidates = { idealJobTitles: false, lastCommentCreatedAt: false };
  const [activeSortCandidates, setActiveSortCandidates] = useState(initSortingCandidates);
  const [isCandidateASC, setIsCandidateASC] = useState(true);
  const resetCandidateSort = () => setActiveSortCandidates(initSortingCandidates);

  const { userProfile } = useContext(ProfileContext);

  const roleQueriesPaginationRef = useRef<CustomPaginationType>(null);
  const candidateQueriesPaginationRef = useRef<CustomPaginationType>(null);

  const { refetch: refetchRoles, data: roleTopicsData, loading: loadingRoles } = useQuery(GET_ROLE_TOPICS, {
    variables: {skip: 0, take: 25, hideResolved: hideRoleResolved, order: {updatedAt: "DESC"}},
    onCompleted: (data) => {
      setJobTopics(data?.jobTopics?.items);
      setRoleHasNext(data?.jobTopics?.pageInfo?.hasNextPage || data?.jobTopics?.pageInfo?.hasPreviousPage);
    }
  });

  const { refetch: refetchCandidates, data: candidateTopicsData, loading: loadingCandidates } = useQuery(GET_CANDIDATE_TOPICS, {
    variables: {skip: 0, take: 25, hideResolved: hideCandidateResolved, order: {updatedAt: "DESC"}},
    onCompleted: (data) => {
      setCandidateTopics(data?.candidateTopics?.items);
      setCandidateHasNext(data?.candidateTopics?.pageInfo?.hasNextPage || data?.candidateTopics?.pageInfo?.hasPreviousPage);
    }
  });

  const [resolveRole] = useMutation(
    CREATE_ROLE_RESOLVING_MESSAGE,
    {
      onCompleted() {
        refetchRoles();
        setShowToast(true);
        setJobTopics(roleTopicsData?.jobTopics?.items);
      },
    }
  );

  const [resolveCandidate] = useMutation(
    CREATE_CANDIDATE_RESOLVING_MESSAGE,
    {
      onCompleted(){
        refetchCandidates();
        setShowToast(true);
        setCandidateTopics(candidateTopicsData?.candidateTopics?.items);
      },
    }
  );

  const runPagination = (skip = 0, take = 25) => {
    const getRolesList = async () => {
      const { data: refreshRoles } = await refetchRoles({skip, take, hideResolved: hideRoleResolved, order: {updatedAt: "DESC"}});
      setJobTopics(refreshRoles?.jobTopics?.items);
      setRoleHasNext(refreshRoles?.jobTopics?.pageInfo?.hasNextPage || refreshRoles?.jobTopics?.pageInfo?.hasPreviousPage);
      roleQueriesPaginationRef.current?.setPagination({
        skip,
        take,
        totalCount: refreshRoles?.jobTopics?.totalCount,
      });
    }

    const getCandidatesList = async () => {
      const { data: refreshCandidates } = await refetchCandidates({skip, take, hideResolved: hideCandidateResolved, order: {updatedAt: "DESC"}});
      setCandidateTopics(refreshCandidates?.candidateTopics?.items);
      setCandidateHasNext(refreshCandidates?.candidateTopics?.pageInfo?.hasNextPage || refreshCandidates?.candidateTopics?.pageInfo?.hasPreviousPage);
      candidateQueriesPaginationRef.current?.setPagination({
        skip,
        take,
        totalCount: refreshCandidates?.candidateTopics?.totalCount,
      });
    }

    if (tabKey === "roles") getRolesList();
    if (tabKey === "candidates") getCandidatesList();
  }

  useEffect(() => {
    runPagination();
  }, [hideRoleResolved, hideCandidateResolved, tabKey, roleHasNext, candidateHasNext]);

  useEffect(() => {
    if (tabKey === "roles" || topicType === "j" || topicType === "jc") {
      setActiveTab("roles");
      if (topicType === undefined) navigate("/messages/roles");
    } else if (tabKey === "candidates" || topicType === "c" || topicType === "cc") {
      setActiveTab("candidates");
      if (topicType === undefined) navigate("/messages/candidates");
    }
  }, [tabKey, topicType]);

  const setActiveTab = (tab: string | null) => {
    if (tab) {
      setTabKey(tab);
    }
  };

  const handleFloatingChat = (item: any) => {
    setIsOpenChat(val => !val);
    setTopicItem(item);
  }

  const handleSortingRoles = (tableTitle: string) => {
    let sortingDataRoles = jobTopics.slice(0);
    sortingDataRoles.sort((a, b) => compareSortingData(a, b, tableTitle));
    setIsRoleASC(value => !value);
    isRoleASC ?
      setJobTopics(sortingDataRoles) :
      setJobTopics(sortingDataRoles.reverse());
    resetRoleSort();
    setActiveSortRoles((prev) => { return { ...prev, [tableTitle]: true }});
  }

  const handleSortingCandidates = (tableTitle: string) => {
    let sortingDataCandidates = candidateTopics.slice(0);
    sortingDataCandidates.sort((a, b) => compareSortingData(a, b, tableTitle));
    setIsCandidateASC(value => !value);
    isCandidateASC ?
      setCandidateTopics(sortingDataCandidates) :
      setCandidateTopics(sortingDataCandidates.reverse());
    resetCandidateSort();
    setActiveSortCandidates((prev) => { return { ...prev, [tableTitle]: true }});
  }

  const handleRoleResolvingMessage = (id: number) => {
    resolveRole({
      variables: {
        jobTopicId: id
      },
    });
  }

  const handleCandidateResolvingMessage = (id: number) => {
    resolveCandidate({
      variables: {
        candidateTopicId: id,
      },
    });
  }

  // Render empty state
  const EmptyState = ({ message }: { message: string }) => (
    <div className={cn(
      "flex flex-col items-center justify-center",
      "py-16 px-6"
    )}>
      <div className={cn(
        "w-16 h-16 rounded-2xl mb-5",
        "bg-gradient-to-br from-slate-100 to-slate-50",
        "dark:from-slate-800 dark:to-slate-800/50",
        "border border-slate-200/60 dark:border-slate-700/60",
        "flex items-center justify-center",
        "shadow-sm"
      )}>
        <Inbox className="w-7 h-7 text-slate-400 dark:text-slate-500" />
      </div>
      <p className={cn(
        "text-base text-slate-600 dark:text-slate-400",
        "font-medium text-center"
      )}>
        {message}
      </p>
      <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
        Check back later for new conversations
      </p>
    </div>
  );

  // Render sorting header for desktop table view
  const SortHeader = ({
    label,
    sortKey,
    isActive,
    isAscending,
    onClick
  }: {
    label: string;
    sortKey: string;
    isActive: boolean;
    isAscending: boolean;
    onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5",
        "px-3 py-1.5 -mx-3 rounded-md",
        "text-xs font-medium uppercase tracking-wider",
        "text-slate-500 dark:text-slate-400",
        "hover:text-teal-600 dark:hover:text-teal-400",
        "hover:bg-teal-50/50 dark:hover:bg-teal-950/30",
        "transition-all duration-200",
        "cursor-pointer select-none",
        isActive && "text-teal-600 dark:text-teal-400"
      )}
    >
      <span>{label}</span>
      {isActive ? (
        <ChevronTableHeader ascending={isAscending} />
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40" />
      )}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header - Matching Settings typography */}
      <header className="mb-8">
        <h1 className="font-serif text-3xl text-slate-900 dark:text-slate-100 tracking-tight mb-2">
          Messages
        </h1>
        <p className="text-base text-slate-600 dark:text-slate-400">
          Manage your role and candidate conversations
        </p>
      </header>

      {/* Tabs Container */}
      <Tabs value={tabKey} onValueChange={setActiveTab} className="w-full">
        {/* Tab Navigation - Refined styling */}
        <div className="mb-6">
          <TabsList className={cn(
            "inline-flex h-11 p-1",
            "bg-slate-100/80 dark:bg-slate-800/60",
            "border border-slate-200/60 dark:border-slate-700/50",
            "rounded-xl"
          )}>
            <TabsTrigger
              value="roles"
              className={cn(
                "h-9 px-4 rounded-lg gap-2",
                "text-sm font-medium",
                "data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700",
                "data-[state=active]:text-slate-900 dark:data-[state=active]:text-white",
                "data-[state=active]:shadow-sm",
                "data-[state=inactive]:text-slate-600 dark:data-[state=inactive]:text-slate-400",
                "transition-all duration-200"
              )}
            >
              <Briefcase className="w-4 h-4" />
              Role Queries
            </TabsTrigger>
            <TabsTrigger
              value="candidates"
              className={cn(
                "h-9 px-4 rounded-lg gap-2",
                "text-sm font-medium",
                "data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700",
                "data-[state=active]:text-slate-900 dark:data-[state=active]:text-white",
                "data-[state=active]:shadow-sm",
                "data-[state=inactive]:text-slate-600 dark:data-[state=inactive]:text-slate-400",
                "transition-all duration-200"
              )}
            >
              <Users className="w-4 h-4" />
              Candidate Queries
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Role Queries Tab */}
        <TabsContent value="roles" className="mt-0">
          <div className={cn(
            "border border-slate-200 dark:border-slate-700",
            "rounded-xl",
            "bg-white dark:bg-slate-900",
            "shadow-sm",
            "overflow-hidden"
          )}>
            {/* Header with toggle and sort */}
            <div className={cn(
              "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",
              "px-5 py-4",
              "border-b border-slate-100 dark:border-slate-800",
              "bg-slate-50/50 dark:bg-slate-800/30"
            )}>
              {/* Show resolved toggle */}
              <div className="flex items-center gap-3">
                <Switch
                  id="show-resolved-roles"
                  checked={!hideRoleResolved}
                  onCheckedChange={() => setHideRoleResolved(val => !val)}
                  className="data-[state=checked]:bg-teal-600"
                />
                <Label
                  htmlFor="show-resolved-roles"
                  className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer select-none"
                >
                  Show resolved
                </Label>
              </div>

              {/* Sort controls - desktop only */}
              {!isMobile && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 dark:text-slate-500 mr-1">Sort by:</span>
                  <SortHeader
                    label="Title"
                    sortKey="jobTitle"
                    isActive={activeSortRoles.jobTitle}
                    isAscending={isRoleASC}
                    onClick={() => handleSortingRoles('jobTitle')}
                  />
                  <SortHeader
                    label="Updated"
                    sortKey="lastCommentCreatedAt"
                    isActive={activeSortRoles.lastCommentCreatedAt}
                    isAscending={isRoleASC}
                    onClick={() => handleSortingRoles('lastCommentCreatedAt')}
                  />
                </div>
              )}
            </div>

            {/* Messages List */}
            <div className="p-5 lg:p-6">
              {/* Loading state */}
              {loadingRoles && (
                <MessagesTableSkeleton rows={5} isMobile={isMobile} />
              )}

              {/* Empty state */}
              {!loadingRoles && jobTopics !== undefined && jobTopics.length === 0 && (
                <EmptyState message="No role queries at this time" />
              )}

              {/* Content */}
              {!loadingRoles && jobTopics !== undefined && jobTopics.map((item, index) => (
                isMobile ? (
                  <MessageCard
                    key={item.id}
                    item={item}
                    index={index}
                    onView={() => handleFloatingChat(item)}
                    onResolve={() => handleRoleResolvingMessage(item.id)}
                    isRole={true}
                  />
                ) : (
                  <MessageRow
                    key={item.id}
                    item={item}
                    index={index}
                    onView={() => handleFloatingChat(item)}
                    onResolve={() => handleRoleResolvingMessage(item.id)}
                    isRole={true}
                  />
                )
              ))}
            </div>

            {/* Pagination */}
            {!loadingRoles && roleHasNext && (
              <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/20">
                <CustomPagination
                  ref={roleQueriesPaginationRef}
                  onPaginationChange={(args) => runPagination(args.skip, args.take)}
                />
              </div>
            )}
          </div>
        </TabsContent>

        {/* Candidate Queries Tab */}
        <TabsContent value="candidates" className="mt-0">
          <div className={cn(
            "border border-slate-200 dark:border-slate-700",
            "rounded-xl",
            "bg-white dark:bg-slate-900",
            "shadow-sm",
            "overflow-hidden"
          )}>
            {/* Header with toggle and sort */}
            <div className={cn(
              "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",
              "px-5 py-4",
              "border-b border-slate-100 dark:border-slate-800",
              "bg-slate-50/50 dark:bg-slate-800/30"
            )}>
              {/* Show resolved toggle */}
              <div className="flex items-center gap-3">
                <Switch
                  id="show-resolved-candidates"
                  checked={!hideCandidateResolved}
                  onCheckedChange={() => setHideCandidateResolved(val => !val)}
                  className="data-[state=checked]:bg-teal-600"
                />
                <Label
                  htmlFor="show-resolved-candidates"
                  className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer select-none"
                >
                  Show resolved
                </Label>
              </div>

              {/* Sort controls - desktop only */}
              {!isMobile && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 dark:text-slate-500 mr-1">Sort by:</span>
                  <SortHeader
                    label="Title"
                    sortKey="idealJobTitles"
                    isActive={activeSortCandidates.idealJobTitles}
                    isAscending={isCandidateASC}
                    onClick={() => handleSortingCandidates('idealJobTitles')}
                  />
                  <SortHeader
                    label="Updated"
                    sortKey="lastCommentCreatedAt"
                    isActive={activeSortCandidates.lastCommentCreatedAt}
                    isAscending={isCandidateASC}
                    onClick={() => handleSortingCandidates('lastCommentCreatedAt')}
                  />
                </div>
              )}
            </div>

            {/* Messages List */}
            <div className="p-5 lg:p-6">
              {/* Loading state */}
              {loadingCandidates && (
                <MessagesTableSkeleton rows={5} isMobile={isMobile} />
              )}

              {/* Empty state */}
              {!loadingCandidates && candidateTopics !== undefined && candidateTopics.length === 0 && (
                <EmptyState message="No candidate queries at this time" />
              )}

              {/* Content */}
              {!loadingCandidates && candidateTopics !== undefined && candidateTopics.map((item, index) => (
                isMobile ? (
                  <MessageCard
                    key={item.id}
                    item={item}
                    index={index}
                    onView={() => handleFloatingChat(item)}
                    onResolve={() => handleCandidateResolvingMessage(item.id)}
                    isRole={false}
                  />
                ) : (
                  <MessageRow
                    key={item.id}
                    item={item}
                    index={index}
                    onView={() => handleFloatingChat(item)}
                    onResolve={() => handleCandidateResolvingMessage(item.id)}
                    isRole={false}
                  />
                )
              ))}
            </div>

            {/* Pagination */}
            {!loadingCandidates && candidateHasNext && (
              <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/20">
                <CustomPagination
                  ref={candidateQueriesPaginationRef}
                  onPaginationChange={(args) => runPagination(args.skip, args.take)}
                />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* User Guide Link */}
      <p className="mt-8 text-center">
        <a
          href={guide}
          target="_blank"
          rel="noreferrer"
          className={cn(
            "inline-flex items-center gap-1.5",
            "text-sm text-slate-500 dark:text-slate-400",
            "hover:text-teal-600 dark:hover:text-teal-400",
            "transition-colors duration-200"
          )}
        >
          <MessageSquare className="w-4 h-4" />
          View the User Guide
        </a>
      </p>

      {/* Floating Chat Modal */}
      <FloatingChatMessage
        isOpenChat={isOpenChat}
        onHideModal={() => setIsOpenChat(false)}
        topic={topicItem}
        userProfile={userProfile}
        client={client}
        isRole={tabKey === "roles"}
      />

      {/* Success Notification */}
      <Notification
        delay={3000}
        toastData={{show: showToast, color: EToastColorStatus.SUCCESS, position: EToastPosition.BOTTOM_END, message: "Query resolved successfully"}}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default Messages;
